/**
 * Previous / next camera view: zoom-bar buttons, Alt+Left / Alt+Right, command palette.
 * OFF — history is not recorded and the editor does not answer those keys.
 */
export const VIEW_HISTORY_ON = false;

/** A camera position worth returning to. */
export interface ViewSnap {
  cx: number;
  cy: number;
  zoom: number;
}

/** How many jumps Back can undo. */
const LIMIT = 20;
/** A pan smaller than this on screen is not a jump. */
const MIN_PX = 2;
/** A zoom change smaller than this ratio is not a jump. */
const MIN_ZOOM = 1.02;

/** True when two views would look the same on screen. */
export function sameView(a: ViewSnap, b: ViewSnap): boolean {
  const z = Math.max(a.zoom, b.zoom);
  const dx = Math.abs(a.cx - b.cx) * z;
  const dy = Math.abs(a.cy - b.cy) * z;
  const ratio = a.zoom > b.zoom ? a.zoom / b.zoom : b.zoom / a.zoom;
  return dx < MIN_PX && dy < MIN_PX && ratio < MIN_ZOOM;
}

/**
 * Back/forward for camera jumps. Pan and wheel zoom are not entries.
 * The list lives with the editor: nothing is written into the canvas.
 */
export class ViewTrail {
  private back: ViewSnap[] = [];
  private forward: ViewSnap[] = [];

  get canBack(): boolean {
    return this.back.length > 0;
  }
  get canForward(): boolean {
    return this.forward.length > 0;
  }

  /**
   * The camera moved from `from` to `to` in one jump.
   * Returns whether Back/Forward availability changed.
   */
  note(from: ViewSnap, to: ViewSnap): boolean {
    if (sameView(from, to)) return false;
    const top = this.back[this.back.length - 1];
    if (!top || !sameView(top, from)) {
      this.back.push({ ...from });
      if (this.back.length > LIMIT) this.back.shift();
    }
    this.forward = [];
    return true;
  }

  /** Previous view. The current one becomes the next Forward step. */
  retreat(current: ViewSnap): ViewSnap | null {
    const prev = this.back.pop();
    if (!prev) return null;
    if (!sameView(current, prev)) {
      this.forward.push({ ...current });
      if (this.forward.length > LIMIT) this.forward.shift();
    }
    return { ...prev };
  }

  /** Next view, after Back. */
  advance(current: ViewSnap): ViewSnap | null {
    const next = this.forward.pop();
    if (!next) return null;
    if (!sameView(current, next)) {
      this.back.push({ ...current });
      if (this.back.length > LIMIT) this.back.shift();
    }
    return { ...next };
  }
}
